import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Box, Chip } from "@mui/material";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { BorderColor, Delete } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { format } from "date-fns";
import {
  doc,
  deleteDoc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { deleteImageAws } from "../utils/awsUtils";

export default function CustomTable({ headers, data, editPath, deletePath, extraActions, resetKey }) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [selectedId, setSelectedId] = React.useState("");
  const [selectedTitle, setSelectedTitle] = React.useState("");
  const [selectedImgId, setSelectedImgId] = React.useState("");
  const [selectedFamilyId, setSelectedFamilyId] = React.useState("");
  const [show, setShow] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  React.useEffect(() => {
    setPage(0);
  }, [resetKey]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const collectionMap = {
    "/delete-event": "events",
    "/delete-article": "articles",
    "/delete-gallery": "gallery",
    "/delete-admin": "users",
    "/delete-notification": "notifications",
    "/delete-author": "authors",
    "/delete-member": "members",
    "/delete-tract": "sbc-tracts",
  };

  const notifyMsgMap = {
    "/delete-event": (t) => `Deleted Event: ${t}`,
    "/delete-article": (t) => `Deleted Article: ${t}`,
    "/delete-gallery": (t) => `Deleted Gallery Image: ${t}`,
    "/delete-author": (t) => `Deleted Author: ${t}`,
    "/delete-member": (t) => `Deleted Member: ${t}`,
    "/delete-tract": (t) => `Deleted Tract: ${t}`,
  };

  const imageCollections = ["events", "articles", "gallery", "authors", "members"];

  const handleDelete = async () => {
    if (!selectedId) return;
    const collectionName = collectionMap[deletePath];
    if (!collectionName) return;

    setDeleting(true);
    try {
      if (selectedImgId && imageCollections.includes(collectionName)) {
        await deleteImageAws(selectedImgId);
      }

      await deleteDoc(doc(db, collectionName, selectedId));

      if (collectionName === "members" && selectedFamilyId) {
        try {
          const familySnap = await getDoc(doc(db, "families", selectedFamilyId));
          if (familySnap.exists()) {
            const remainingMembers = (familySnap.data().members || []).filter((m) => m.id !== selectedId);
            if (remainingMembers.length === 0) {
              await deleteDoc(doc(db, "families", selectedFamilyId));
            } else {
              await updateDoc(doc(db, "families", selectedFamilyId), {
                members: remainingMembers,
                familyCount: remainingMembers.length,
              });
            }
          }
        } catch (familyErr) {
          console.error(familyErr);
        }
      }

      const msgFn = notifyMsgMap[deletePath];
      if (msgFn) {
        const userName = auth.currentUser?.email || "Admin";
        await addDoc(collection(db, "notifications"), {
          name: userName,
          message: msgFn(selectedTitle),
          createdAt: serverTimestamp(),
        });
      }

      toast("Deleted successfully", { type: "success" });
      setShow(false);
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      console.error(err.message);
      toast("Error deleting item: " + err.message, { type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const STATUS_CONFIG = {
    pending:  { bg: "#fffbeb", color: "#d97706", label: "Pending" },
    approved: { bg: "#ecfdf5", color: "#059669", label: "Approved" },
    declined: { bg: "#fef2f2", color: "#dc2626", label: "Declined" },
  };

  const formatCellValue = (headerChild, value) => {
    if (headerChild.type === "image") {
      return value ? (
        <img
          src={value}
          alt="thumb"
          style={{ height: 42, width: 42, objectFit: "cover", borderRadius: 8, border: "2px solid #f0f0f0" }}
        />
      ) : (
        <div style={{ height: 42, width: 42, borderRadius: 8, backgroundColor: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 10, color: "#999" }}>No img</span>
        </div>
      );
    }
    if (headerChild.type === "status") {
      const cfg = STATUS_CONFIG[value] || STATUS_CONFIG.pending;
      return (
        <span
          style={{
            backgroundColor: cfg.bg,
            color: cfg.color,
            padding: "3px 10px",
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 600,
            fontFamily: "Abel",
            whiteSpace: "nowrap",
          }}
        >
          {cfg.label}
        </span>
      );
    }
    if (headerChild.type === "link") {
      return value ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            color: "#000C66",
            fontWeight: 600,
            textDecoration: "underline",
            whiteSpace: "nowrap",
          }}
        >
          View / Download
        </a>
      ) : (
        "—"
      );
    }
    if (headerChild.type === "truncate") {
      return (
        <span
          title={value || ""}
          style={{ display: "block", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {value || "—"}
        </span>
      );
    }
    if (headerChild.id === "createdAt" || headerChild.id === "eventdate") {
      return value ? format(new Date(value), "d MMM yyyy") : "—";
    }
    if (headerChild.id === "role") {
      return (
        <Chip
          label={value}
          size="small"
          sx={{
            backgroundColor: value === "superadmin" ? "#000C66" : "#e8f5e9",
            color: value === "superadmin" ? "#FFC000" : "#2e7d32",
            fontFamily: "Abel",
            fontWeight: 600,
            fontSize: 11,
          }}
        />
      );
    }
    return value ?? "—";
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none", borderRadius: 0 }}>
      <TableContainer sx={{ maxHeight: 640, overflowX: "auto" }}>
        <Table stickyHeader aria-label="data table" size="small">
          <TableHead>
            <TableRow>
              {headers.map((h, i) => (
                <TableCell
                  key={i}
                  align={h.align || "left"}
                  style={{ minWidth: h.minWidth, backgroundColor: "#f8faff" }}
                >
                  <span className="text-xs font-bold uppercase tracking-wider text-[#000C66] font-aref">
                    {h.label}
                  </span>
                </TableCell>
              ))}
              <TableCell style={{ backgroundColor: "#f8faff", minWidth: 100 }}>
                <span className="text-xs font-bold uppercase tracking-wider text-[#000C66] font-aref">
                  Actions
                </span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headers.length + 1} align="center" sx={{ py: 6 }}>
                  <span className="text-gray-400 font-abel text-sm">No records found</span>
                </TableCell>
              </TableRow>
            ) : (
              data
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, ri) => (
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={ri}
                    sx={{ "&:hover": { backgroundColor: "#f8faff" }, transition: "background 0.15s" }}
                  >
                    {headers.map((h, ci) => (
                      <TableCell key={ci} align={h.align || "left"} sx={{ py: 1.5 }}>
                        <span className="font-abel text-sm text-gray-700">
                          {formatCellValue(h, row[h.id])}
                        </span>
                      </TableCell>
                    ))}
                    <TableCell sx={{ py: 1.5 }}>
                      <Box className="flex items-center gap-1 flex-wrap">
                        {editPath && (
                          <Link to={`${editPath}/${row._id}`}>
                            <IconButton size="small" sx={{ color: "#000C66", "&:hover": { backgroundColor: "#eef2ff" } }}>
                              <BorderColor fontSize="small" />
                            </IconButton>
                          </Link>
                        )}
                        {row.role !== "superadmin" && deletePath && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedId(row._id);
                              setSelectedTitle(row.title || row.name || "");
                              setSelectedImgId(row.imgId || "");
                              setSelectedFamilyId(row.familyId || "");
                              setShow(true);
                            }}
                            sx={{ color: "#dc2626", "&:hover": { backgroundColor: "#fef2f2" } }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                        {extraActions && extraActions(row)}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 100]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ borderTop: "1px solid #f0f0f0", fontFamily: "Abel" }}
      />

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                <Delete className="text-red-500" style={{ fontSize: 28 }} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 font-aref">Confirm Delete</h3>
              {selectedTitle && (
                <p className="text-gray-500 text-sm font-abel mt-1">
                  Delete <span className="font-semibold text-gray-700">"{selectedTitle}"</span>?
                </p>
              )}
              <p className="text-red-400 text-xs font-abel mt-2">This action cannot be undone.</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShow(false)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-700 font-abel font-medium hover:bg-gray-50 transition"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white font-abel font-semibold hover:bg-red-600 transition disabled:opacity-60"
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Paper>
  );
}
